import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight, Plus } from "lucide-react";
import { REFERENCES } from "../../data/content";
import { Reveal } from "../motion/Motion";

const spanClass = (span) => {
  if (span === "tall") return "row-span-2";
  if (span === "wide") return "sm:col-span-2";
  return "";
};

export default function References() {
  const [active, setActive] = useState(null);

  useEffect(() => {
    const onKey = (e) => {
      if (active === null) return;
      if (e.key === "Escape") setActive(null);
      if (e.key === "ArrowRight") setActive((a) => (a + 1) % REFERENCES.length);
      if (e.key === "ArrowLeft") setActive((a) => (a - 1 + REFERENCES.length) % REFERENCES.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [active]);

  return (
    <section
      id="referenciak"
      data-testid="references-section"
      className="bg-[#F3F4F6] py-20 md:py-32"
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-12">
        <div className="mb-10 max-w-2xl md:mb-16">
          <Reveal>
            <p className="mb-5 flex items-center gap-3 font-mono text-xs uppercase tracking-[0.25em] text-brand-accent">
              <span className="h-px w-10 bg-brand-accent" />
              Referenciák
            </p>
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className="font-heading text-3xl font-extrabold leading-[1.08] tracking-tight text-brand-primary sm:text-4xl lg:text-5xl">
              Kivitelezéseink, amelyek magukért beszélnek
            </h2>
          </Reveal>
        </div>

        <div className="grid auto-rows-[170px] grid-cols-1 gap-3 sm:auto-rows-[220px] sm:grid-cols-2 sm:gap-4 lg:grid-cols-3">
          {REFERENCES.map((ref, i) => (
            <Reveal
              key={i}
              delay={(i % 3) * 0.08}
              className={`${spanClass(ref.span)} h-full`}
            >
              <button
                data-testid={`reference-item-${i}`}
                onClick={() => setActive(i)}
                className="group relative h-full w-full overflow-hidden bg-brand-primary"
              >
                <img
                  src={ref.url}
                  alt={ref.title}
                  className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-brand-primary/0 transition-colors duration-500 group-hover:bg-brand-primary/50" />
                <div className="absolute inset-0 flex flex-col items-start justify-end p-6 opacity-0 transition-opacity duration-500 group-hover:opacity-100">
                  <span className="grid h-10 w-10 place-items-center bg-brand-accent text-white">
                    <Plus size={20} />
                  </span>
                  <span className="mt-3 text-left font-heading text-lg font-bold text-white">
                    {ref.title}
                  </span>
                </div>
              </button>
            </Reveal>
          ))}
        </div>
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {active !== null && (
          <motion.div
            data-testid="lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] flex items-center justify-center bg-brand-primary/95 p-4 backdrop-blur-sm"
            onClick={() => setActive(null)}
          >
            <button
              data-testid="lightbox-close"
              className="absolute right-5 top-5 grid h-12 w-12 place-items-center text-white transition-colors hover:text-brand-accent"
              onClick={() => setActive(null)}
              aria-label="Bezárás"
            >
              <X size={30} />
            </button>
            <button
              data-testid="lightbox-prev"
              className="absolute left-4 grid h-12 w-12 place-items-center text-white transition-colors hover:text-brand-accent md:left-10"
              onClick={(e) => {
                e.stopPropagation();
                setActive((a) => (a - 1 + REFERENCES.length) % REFERENCES.length);
              }}
              aria-label="Előző"
            >
              <ChevronLeft size={40} />
            </button>
            <motion.div
              key={active}
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.35 }}
              className="max-h-[85vh] max-w-4xl"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={REFERENCES[active].url}
                alt={REFERENCES[active].title}
                className="max-h-[80vh] w-full object-contain"
              />
              <p className="mt-4 text-center font-heading text-lg font-semibold text-white">
                {REFERENCES[active].title}
              </p>
            </motion.div>
            <button
              data-testid="lightbox-next"
              className="absolute right-4 grid h-12 w-12 place-items-center text-white transition-colors hover:text-brand-accent md:right-10"
              onClick={(e) => {
                e.stopPropagation();
                setActive((a) => (a + 1) % REFERENCES.length);
              }}
              aria-label="Következő"
            >
              <ChevronRight size={40} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
